import React, { useState } from "react";
import { Loader2, Check, AlertTriangle, Info, Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import AIAuditBadge from "@/components/AIAuditBadge";
import KaComparison from "@/components/KaComparison";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";

const VERDICTS = {
  bygglov: { label: "Bygglov krävs", color: "bg-amber-50 text-amber-800 border-amber-200", icon: AlertTriangle },
  anmälan: { label: "Anmälan räcker", color: "bg-blue-50 text-blue-800 border-blue-200", icon: Info },
  attefall: { label: "Attefall – endast anmälan", color: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: Check },
  lovbefriat: { label: "Lovbefriat", color: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: Check },
};

const ACTIVITY_PRESETS = [
  "Altan", "Garage", "Carport", "Attefallshus", "Tillbyggnad", "Pool",
  "Plank/Mur", "Bygga om vind", "Fasadändring", "Friggebod", "Solceller", "Annat",
];

export default function PermitCheck() {
  const [form, setForm] = useState({
    activity_type: "",
    size: "",
    location: "",
    boundary_distance: "",
    height: "",
    within_plan: "ja",
    extra: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.activity_type) { toast.warning("Välj typ av åtgärd"); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/ai/permit-check", form);
      setResult(data);
      trackEvent("bygglovsbedomning", { verdict: data.verdict, activity: form.activity_type });
    } catch (err) {
      toast.error("Bedömningen misslyckades, försök igen");
    } finally {
      setLoading(false);
    }
  };

  const verdict = result?.verdict;
  const v = verdict ? VERDICTS[verdict] || VERDICTS.bygglov : null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in-up">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-blue-700 text-white flex items-center justify-center rounded-sm">
          <Sparkles className="w-6 h-6 stroke-[1.5]" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Bygglovsguide</div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>Behöver jag bygglov?</h1>
        </div>
      </div>
      <p className="mt-3 text-zinc-600 max-w-2xl">Beskriv din åtgärd så ger AI:n en bedömning: <strong>bygglov</strong>, <strong>anmälan</strong>, <strong>Attefall</strong> eller <strong>lovbefriat</strong>.</p>

      <div className="mt-8 grid lg:grid-cols-5 gap-6">
        <form onSubmit={submit} className="lg:col-span-3 bg-white border border-zinc-200 rounded-sm p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Vad ska du bygga?</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {ACTIVITY_PRESETS.map((a) => (
                <button
                  key={a}
                  type="button"
                  data-testid={`activity-${a}`}
                  onClick={() => set("activity_type")({ target: { value: a } })}
                  className={`px-3 py-1.5 text-xs font-medium border rounded-sm transition ${
                    form.activity_type === a ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 hover:bg-zinc-100"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Storlek (kvm)</label>
              <input data-testid="permit-size" value={form.size} onChange={set("size")} placeholder="t.ex. 12"
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Höjd (m)</label>
              <input data-testid="permit-height" value={form.height} onChange={set("height")} placeholder="t.ex. 3.0"
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Avstånd till tomtgräns (m)</label>
              <input data-testid="permit-boundary" value={form.boundary_distance} onChange={set("boundary_distance")} placeholder="t.ex. 4.5"
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Inom detaljplan?</label>
              <select data-testid="permit-within-plan" value={form.within_plan} onChange={set("within_plan")}
                className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm bg-white">
                <option value="ja">Ja</option>
                <option value="nej">Nej</option>
                <option value="vetEj">Vet ej</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Adress / fastighet</label>
            <input data-testid="permit-location" value={form.location} onChange={set("location")} placeholder="t.ex. Storgatan 12, Stockholm"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Övrigt</label>
            <textarea rows={3} data-testid="permit-extra" value={form.extra} onChange={set("extra")} placeholder="Material, närhet till strand, kulturmiljö m.m."
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm resize-none" />
          </div>

          <button type="submit" data-testid="permit-submit" disabled={loading}
            className="w-full bg-blue-700 hover:bg-blue-800 text-white py-3 rounded-sm font-medium text-sm flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> AI analyserar...</> : <>Få bedömning <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>

        <div className="lg:col-span-2 space-y-4">
          {!result && !loading && (
            <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-6 text-sm text-zinc-600">
              <div className="font-semibold text-zinc-900 mb-2">Så fungerar bedömningen</div>
              AI:n baserar svaret på PBL & Boverkets regler. Bedömningen är vägledande – kontakta alltid kommunen för slutgiltigt besked.
            </div>
          )}
          {loading && (
            <div className="bg-white border border-zinc-200 rounded-sm p-6 flex items-center gap-3 text-sm text-zinc-600">
              <Loader2 className="w-4 h-4 animate-spin" /> Bedömer mot PBL/BBR...
            </div>
          )}
          {result && v && (
            <div className="space-y-3" data-testid="permit-result">
              <div className={`p-5 border rounded-sm ${v.color}`}>
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold">
                  <v.icon className="w-4 h-4" /> Bedömning
                </div>
                <div className="mt-2 text-xl font-semibold" style={{ fontFamily: "Outfit" }}>{v.label}</div>
                {result.headline && <p className="mt-2 text-sm">{result.headline}</p>}
                {typeof result.confidence === "number" && (
                  <div className="mt-3 text-xs">Säkerhet: {Math.round(result.confidence * 100)}%</div>
                )}
              </div>
              {result.reasoning && (
                <div className="bg-white border border-zinc-200 rounded-sm p-5">
                  <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Varför</div>
                  <p className="mt-2 text-sm text-zinc-700 whitespace-pre-line">{result.reasoning}</p>
                </div>
              )}
              {result.next_steps?.length > 0 && (
                <div className="bg-white border border-zinc-200 rounded-sm p-5">
                  <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Nästa steg</div>
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {result.next_steps.map((s, i) => (
                      <li key={i} className="flex gap-2"><Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" /> {s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.law_refs?.length > 0 && (
                <div className="text-xs text-zinc-500">
                  <strong>Lagrum:</strong> {result.law_refs.join(", ")}
                </div>
              )}
              {result.warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-sm p-3 text-xs space-y-1">
                  {result.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}
              {result.audit_id && (
                <div className="flex justify-end">
                  <AIAuditBadge auditId={result.audit_id} kind="permit_check" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {result && <KaComparison />}
    </div>
  );
}
